'use client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useState } from 'react';
import StoreProvider from '@/components/common/StoreProvider';
import { cleanupWebSocket } from '@/lib/services/websocket.service';
import { ConfigProvider, theme } from 'antd';
import enUs from 'antd/lib/locale/en_US';
import zhCN from 'antd/lib/locale/zh_CN';

import { useTheme } from 'next-themes';
import { fallbackLng, Language, LanguageParams } from '@/i18n/settings';
import { Locale } from 'antd/es/locale';
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google';
import { OrderResultToaster } from '@/components/ui/orderResultToaster';
import { Toaster } from '@/components/ui/toaster';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { usePathname, useSearchParams } from 'next/navigation';
import { PreferenceSync } from './PreferenceSync';
import { DeviceRedirect } from './DeviceRedirect';
import { arbitrum } from 'wagmi/chains';
import { WagmiProvider } from 'wagmi';
import { wagmiConfig } from '@/lib/config/wagmi';
import {
  RainbowKitProvider,
  darkTheme,
  midnightTheme,
  lightTheme,
} from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';

NProgress.configure({
  showSpinner: false, // 关闭加载旋转器
  minimum: 0.3, // 最小显示百分比
  easing: 'ease', // 动画模式
  speed: 800, // 动画速度
  trickleSpeed: 100, // 自动递增间隔
});

export const LANGUAGE_KEY = '__dex_web_language';
const languageMap: { [key in Language]: Locale } = {
  'en-US': enUs,
  'zh-CN': zhCN,
};
export function ClientProvider({
  children,
  locale,
}: PropsWithChildren & LanguageParams) {
  const [queryClient] = useState(() => new QueryClient());
  const { resolvedTheme } = useTheme();
  const [rendered, setRendered] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    NProgress.done();

    // return () => {
    //   console.log('start, time:', new Date().toISOString());
    //   NProgress.start();
    // };
  }, [pathname, searchParams]);

  useEffect(() => {
    setRendered(true);
  }, [resolvedTheme]);
  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, locale ?? fallbackLng);
  }, [locale]);

  // 清理WebSocket连接当应用卸载时
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupWebSocket();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupWebSocket();
    };
  }, []);

  if (!rendered) return null;
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          initialChain={arbitrum}
          modalSize="compact"
          theme={{
            lightMode: lightTheme(),
            darkMode: darkTheme(),
          }}
        >
          {/* <PreferenceSync /> */}
          {/* <DeviceRedirect /> */}
          <StoreProvider>
            <ConfigProvider
              theme={{
                algorithm:
                  resolvedTheme === 'dark'
                    ? theme.darkAlgorithm
                    : theme.defaultAlgorithm,
                components: {
                  Table: {
                    headerBorderRadius: 0,
                    headerSplitColor: 'transparent',
                    headerBg: 'transparent',
                    cellPaddingInline: 12,
                    cellPaddingInlineSM: 4,
                    borderColor: 'transparent',
                    colorBgContainer: 'transparent',
                    stickyScrollBarBg: 'transparent',
                  },
                },
              }}
              locale={languageMap[locale]}
            >
              {children}
              {process.env.NEXT_PUBLIC_ENABLE_GOOGLE_ANALYTICS === '1' && (
                <>
                  <GoogleAnalytics
                    gaId={process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID ?? ''}
                  />
                  <GoogleTagManager
                    gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID ?? ''}
                  />
                </>
              )}
              <OrderResultToaster />
              <Toaster />
            </ConfigProvider>
          </StoreProvider>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
export const getRequestLanguage = (): Language => {
  try {
    const locale = global.localStorage.getItem(LANGUAGE_KEY) as Language | null;
    return locale ?? fallbackLng;
  } catch {
    return fallbackLng;
  }
};
